import styles from "./TagInput.module.css";
import PropTypes from "prop-types";
import { useState, useCallback, useEffect, useRef } from "react";
import { useClickAway, useDebounce } from "react-use";
import { Button, CircleLoader, TextInput } from "@wwg/ui";
import { useFreshState } from "shared/hooks/useFreshState";
import { ProductTag } from "../ProductTag/ProductTag";
import { Modal } from "../Modal/Modal";
import { ModalContent } from "../Modal/Modal";
import { ModalControls } from "../Modal/Modal";
import { ModalHeader } from "../Modal/Modal";
import { useTranslation } from "react-i18next";

/**
 * Generate a string unique per tag combination to compare and see
 * if we should consider the tags changed.
 */
const makeHash = (tags) =>
  tags
    .map((t) => t.trim().toLowerCase())
    .filter((t) => t !== "")
    .sort()
    .join(",");

/**
 * Input box for searching and picking tags.
 */
export function TagInput({ value, onChange, getSuggestions, isRequired, ...props }) {
  const {t} = useTranslation();
  const containerRef = useRef();
  const [tags, setTags] = useFreshState(value);
  const [inputValue, setInputValue] = useState("");
  const [stagedTag, setStagedTag] = useState(null);
  const [isDeletingTag, setIsDeletingTag] = useState(false);

  const addTag = useCallback(
    (value) => {
      // console.log(value);
      const newTags = [...tags, value];
      setTags(newTags);
      onChange(newTags);
    },
    [tags]
  );

  const removeTag = useCallback(
    (value) => {
      const newTags = tags.filter((t) => t !== value);
      setTags(newTags);
      onChange(newTags);
    },
    [tags]
  );

  const [suggestionsAreOpen, setSuggestionsAreOpen] = useState(false);
  const [suggestionsAreLoading, setSuggestionsAreLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useDebounce(
    () => {
      if (inputValue.length > 0 && typeof getSuggestions === "function") {
        setSuggestionsAreLoading(true);
        getSuggestions(inputValue)
          .then((values) => {
            setSuggestions(values.filter((t) => !tags.includes(t)));
          })
          .finally(() => {
            setSuggestionsAreLoading(false);
          });
      }
    },
    300,
    [inputValue]
  );

  useEffect(() => {
    if (inputValue.length > 0) {
      setSuggestionsAreOpen(true);
    } else {
      setSuggestionsAreOpen(false);
      setSuggestions([]);
    }
  }, [inputValue]);

  useClickAway(containerRef, () => {
    setSuggestionsAreOpen(false);
  });

  return (
    <div className={styles.container} ref={containerRef}>
      <form
        className={styles.inputGroup}
        onSubmitCapture={(e) => {
          // console.log(e);
          e.preventDefault();
          if (inputValue.length > 0) {
            addTag(inputValue);
            setInputValue("");
          }
        }}
      >
        <TextInput
          className={styles.input}
          placeholder={t("tags.enterProductName")}
          maxLength={30}
          {...props}
          value={inputValue}
          autoComplete={false}
          autoFill={false}
          onFocus={() => {
            if (suggestions.length > 0) {
              setSuggestionsAreOpen(true);
            }
          }}
          onChange={(e) => {
            setInputValue(e.target.value);
          }}
        />
        {inputValue.length > 0 && (
          <Button className={styles.addButton} type="submit">
            {t("tags.addTag")}
          </Button>
        )}
        {suggestionsAreOpen && (
          <div className={styles.suggestionsPanel}>
            {suggestionsAreLoading ? (
              <div className={styles.loader}>
                <CircleLoader />
              </div>
            ) : suggestions.length === 0 ? (
              <>{t("tags.noTagsFound")}</>
            ) : (
              <>
                <header>
                  <span>{t("tags.chooseATag")}</span>
                </header>

                <div className={styles.tags}>
                  {suggestions.map((t, i) => (
                    <button
                      key={i}
                      className={styles.tagButton}
                      onClickCapture={(e) => {
                        e.preventDefault();
                        addTag(t);
                        setInputValue("");
                      }}
                    >
                      <ProductTag name={t} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </form>
      <div className={styles.tags}>
        {tags.map((t, i) => (
          <button
            key={i}
            className={styles.tagButton}
            onClickCapture={(e) => {
              e.preventDefault();
              setStagedTag(t);
              setIsDeletingTag(true);
            }}
          >
            <ProductTag name={t} />
          </button>
        ))}
        {isRequired && tags.length === 0 ? (
          <div style={{ color: "red" }}>{t("tags.tagRequired")}</div>
        ) : (
          <></>
        )}
      </div>

      <Modal
        isOpen={isDeletingTag}
        onClose={() => {
          setIsDeletingTag(false);
        }}
      >
        <ModalContent>
          <ModalHeader>{t("tags.removeTag")}</ModalHeader>

          <p>{t("tags.areYouSure", { stagedTag })}</p>
        </ModalContent>

        <ModalControls>
          <Button
            buttonStyle="hollow"
            onClick={() => {
              setIsDeletingTag(false);
            }}
          >
            {t("tags.Cancel")}
          </Button>
          <Button
            buttonStyle="danger"
            onClick={() => {
              removeTag(stagedTag);
              setIsDeletingTag(false);
            }}
          >
            {t("tags.Remove")}
          </Button>
        </ModalControls>
      </Modal>
    </div>
  );
}

TagInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func,

  /**
   * Async function to take a search term and return a list of matching tags.
   * A loading indicator is displayed until the promise resolves.
   */
  getSuggestions: PropTypes.func.isRequired,
};
